import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, Flower, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MerchSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/merch');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails({
            amount: response.data.amount_total / 100,
            productName: response.data.metadata?.product_name || 'Product'
          });
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    };

    pollPaymentStatus();
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Flower size={32} weight="fill" className="text-primary" />
          <span className="text-2xl font-heading font-bold">Bloom & Bond</span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl p-8 md:p-12 text-center"
        >
          {status === 'checking' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
              <h1 className="text-2xl font-heading font-bold mb-2">Processing Your Order...</h1>
              <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle size={64} weight="fill" className="text-accent mx-auto mb-6" />
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">Order Confirmed! 🎉</h1>
              {orderDetails && (
                <>
                  <div className="bg-muted rounded-2xl p-6 mb-6 text-left">
                    <div className="flex items-center gap-3 mb-4">
                      <Package size={32} weight="fill" className="text-primary" />
                      <div>
                        <h3 className="font-heading font-semibold">{orderDetails.productName}</h3>
                        <p className="text-sm text-muted-foreground">Quantity: 1</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t border-border">
                      <span className="font-medium">Total Paid:</span>
                      <span className="text-2xl font-heading font-bold text-primary">${orderDetails.amount.toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-8">
                    Thank you for your purchase! A confirmation email with shipping details has been sent to you.
                  </p>
                </>
              )}
              <div className="flex gap-3 justify-center">
                <Button
                  data-testid="continue-shopping-button"
                  onClick={() => navigate('/merch')}
                  variant="outline"
                  className="rounded-full font-bold px-8"
                >
                  Continue Shopping
                </Button>
                <Button
                  data-testid="return-dashboard-button"
                  onClick={() => navigate('/dashboard')}
                  className="rounded-full bg-primary hover:bg-primary-hover font-bold px-8"
                >
                  Return to Dashboard
                </Button>
              </div>
            </>
          )}

          {(status === 'expired' || status === 'timeout') && (
            <>
              <div className="text-destructive text-5xl mb-6">⚠️</div>
              <h1 className="text-2xl font-heading font-bold mb-4">Payment Issue</h1>
              <p className="text-muted-foreground mb-8">
                We couldn't confirm your payment. Please check your email or try again.
              </p>
              <Button
                data-testid="try-again-button"
                onClick={() => navigate('/merch')}
                className="rounded-full bg-primary hover:bg-primary-hover font-bold px-8"
              >
                Back to Merch
              </Button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}